import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { UserModel } from '../models/user.model';
import * as xlsx from 'xlsx-js-style';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';
import { TIMEZONES } from 'src/app/_shared/core/constants/stealth.constants';
import { MonthlyDashboardFilterModel } from 'src/app/main/core/models/monthly-dashboard.model';

export enum ExportLookUp {
  CSV = 1,
  XLSX = 2,
  PDF = 3
}

export class DataSheet {
  sheetName: string
  data: Array<any>
  columns: Array<any>
}

export class DataOrder {
  csv: boolean
  excel: boolean
  pdf: boolean
}

export class ExportColumns {
  columnName: string;
  field: string;
  header: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportMonthlyDashboardService {
  exportFile$: Observable<ExportLookUp>;
  exportFileSubject: BehaviorSubject<ExportLookUp>;
  isExporting$: Observable<boolean>;
  isExportingSubject: BehaviorSubject<boolean>;
  isExportingCSV$: Observable<boolean>;
  isExportingCSVSubject: BehaviorSubject<boolean>;
  isExportingExcel$: Observable<boolean>;
  isExportingExcelSubject: BehaviorSubject<boolean>;
  isIncidentsLoaded$ = new BehaviorSubject<boolean>(false);
  userName: string;
  constructor(
    private readonly authService: AuthService
  ) {
    this.exportFileSubject = new BehaviorSubject<ExportLookUp>(undefined);
    this.exportFile$ = this.exportFileSubject.asObservable();
    this.isExportingSubject = new BehaviorSubject<boolean>(false);
    this.isExporting$ = this.isExportingSubject.asObservable();

    this.isExportingCSVSubject = new BehaviorSubject<boolean>(false);
    this.isExportingCSV$ = this.isExportingCSVSubject.asObservable();

    this.isExportingExcelSubject = new BehaviorSubject<boolean>(false);
    this.isExportingExcel$ = this.isExportingExcelSubject.asObservable();

    this.authService.currentUser$.subscribe((user: UserModel) => {
      if (user)
        this.userName = user.name + ' ' + user.surname;
    });
  }

  exportObservable(type: ExportLookUp) {
    if (type == ExportLookUp.CSV) {
      this.isExportingCSVSubject.next(true);
    }
    else if (type == ExportLookUp.XLSX) {
      this.isExportingExcelSubject.next(true);
    }
    this.exportFileSubject.next(type);
  }

  export(exportType: number, filename: string, sheet: DataSheet, filter: MonthlyDashboardFilterModel) {
    if (exportType === ExportLookUp.CSV) {
      this.exportCSV(filename, sheet, filter);
    }
    else if (exportType === ExportLookUp.XLSX) {
      this.exportXLSX(filename, sheet, filter);
    }
  }

  exportCSV(filename: string, sheet: DataSheet, filter: MonthlyDashboardFilterModel) {
    const table = [];

    this.processCSVSheetData(sheet, table);

    const worksheet = xlsx.utils.json_to_sheet(table);
    let csvOutput: string = xlsx.utils.sheet_to_csv(worksheet);
    let outPutDate = new Date();
    let finalcsvOutput = `Change Dashboard\nUser:,${this.userName},\nDate:,${moment(outPutDate).format('MM/DD/YYYY hh:mm a')}\n\n`

    if (filter.startDate || filter.endDate || filter.submitedStartDate || filter.submitedEndDate) {
      if (filter.startDate)
        finalcsvOutput += `Requested From Date:,${moment(filter.startDate).format('MM/DD/YYYY')}\n`;
      if (filter.endDate)
        finalcsvOutput += `Requested To Date:,${moment(filter.endDate).format('MM/DD/YYYY')}\n`;
      if (filter.submitedStartDate)
        finalcsvOutput += `Submitted From Date:,${moment(filter.submitedStartDate).format('MM/DD/YYYY')}\n`;
      if (filter.submitedEndDate)
        finalcsvOutput += `Submitted To Date:,${moment(filter.submitedEndDate).format('MM/DD/YYYY')}\n`;
      finalcsvOutput += `\n`;
    }

    finalcsvOutput = finalcsvOutput + csvOutput;
    this.saveAsCSVFile(finalcsvOutput, filename);
  }

  private processCSVSheetData(sheet: DataSheet, table: object[]) {
    for (const row of sheet.data) {
      const tempRow: object = {};

      for (const { header, field } of sheet.columns) {
        if (header.includes("Date")) {
          tempRow[header] = row[field] ? this.transformDateWithTimezone(row[field], row['zonename__c']) : row[field];
        } else {
          tempRow[header] = row[field]?.toString();
        }
      }

      table.push(tempRow);
    }
  }

  saveAsCSVFile(buffer: any, fileName: string): void {
    let currentDt = new Date();
    let CSV_TYPE = 'text/csv;charset=utf-8';
    let CSV_EXTENSION = '.csv';
    const data: Blob = new Blob([buffer], {
      type: CSV_TYPE
    });
    FileSaver.saveAs(data, `${fileName}_${currentDt.toISOString().split('T')[0]}_${currentDt.getTime()}${CSV_EXTENSION}`);
    this.isExportingCSVSubject.next(false);
  }

  exportXLSX(filename: string, sheet: DataSheet, filter: MonthlyDashboardFilterModel): void {
    const data = [];

    const table = this.processXLSXSheetData(sheet);
    data.push(table);

    let headings: string[] = [];
    for (const column of sheet.columns) {
      headings.push(column["header"]);
    }

    const wb = xlsx.utils.book_new();
    const ws: xlsx.WorkSheet = xlsx.utils.json_to_sheet([]);

    xlsx.utils.sheet_add_aoa(ws, [["Change Dashboard"]]);
    xlsx.utils.sheet_add_aoa(ws, [["User:", this.userName]], { origin: 'A2' });
    let outPutDate = new Date();
    xlsx.utils.sheet_add_aoa(ws, [["Date:", moment(outPutDate).format('MM/DD/YYYY hh:mm a')]], { origin: 'A3' });

    let rowIndex = 5;
    if (filter.startDate || filter.endDate || filter.submitedStartDate || filter.submitedEndDate) {

      if (filter.startDate) {
        xlsx.utils.sheet_add_aoa(ws, [["Requested From Date:", moment(filter.startDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
        rowIndex++;
      }

      if (filter.endDate) {
        xlsx.utils.sheet_add_aoa(ws, [["Requested To Date:", moment(filter.endDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
        rowIndex++;
      }

      if (filter.submitedStartDate) {
        xlsx.utils.sheet_add_aoa(ws, [["Submitted From Date:", moment(filter.submitedStartDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
        rowIndex++;
      }

      if (filter.submitedEndDate) {
        xlsx.utils.sheet_add_aoa(ws, [["Submitted To Date:", moment(filter.submitedEndDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
        rowIndex++;
      }

      rowIndex++;

    }

    xlsx.utils.sheet_add_json(ws, [headings], { origin: `A${rowIndex}`, skipHeader: true });
    let headerIndex = rowIndex;
    rowIndex++;
    xlsx.utils.sheet_add_json(ws, data[0], { origin: `A${rowIndex}`, skipHeader: true });
    const colNames = [`A${headerIndex}`, `B${headerIndex}`, `C${headerIndex}`, `D${headerIndex}`];
    for (const itm of colNames) {
      if (ws[itm]) {
        ws[itm].s = {
          font: { bold: true }
        };
      }
    }

    xlsx.utils.book_append_sheet(wb, ws, sheet.sheetName);

    const excelBuffer: any = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, filename);
  }

  private processXLSXSheetData(sheet: DataSheet): object[] {
    const table = [];

    for (const row of sheet.data) {
      const tempRow: object = {};

      for (const { header, field } of sheet.columns) {
        if (header.includes("Date")) {
          tempRow[header] = row[field] ? this.transformDateWithTimezone(row[field], row['zonename__c']) : row[field];
        } else {
          tempRow[header] = row[field]?.toString();
        }
      }

      table.push(tempRow);
    }

    return table;
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    let currentDt = new Date();
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, `${fileName}_${currentDt.toISOString().split('T')[0]}_${currentDt.getTime()}${EXCEL_EXTENSION}`);
    this.isExportingExcelSubject.next(false);
  }

  private transformDateWithTimezone(incidentDate: string, zoneName: string) {
    if (incidentDate && zoneName) {
      const timeZone = TIMEZONES[zoneName] || "UTC";
      return moment.tz(incidentDate, timeZone).format('MM/DD/YYYY');
    }
    return incidentDate;
  }
}
